import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import TreatmentSteps from './components/TreatmentSteps';
import TreatmentResults from './components/TreatmentResults';
import { useTreatmentURLState, TREATMENT_VIEWS } from './hooks/useTreatmentURLState';
import { useTreatmentEnrichedData } from './hooks/useTreatmentEnrichedData';
import { updateDynamicBreadcrumb } from '@client/utils/page_header';
import { usePageMeta } from '@client/hooks/usePageMeta';

/**
 * Updates the breadcrumb based on the current view
 */
const updateBreadcrumb = (isResultsView) => {
  updateDynamicBreadcrumb(isResultsView ? 'Résultats de recherche' : '');
};

const TreatmentSearch = () => {
  const { t } = useTranslation();
  usePageMeta({
    title: t('seo|treatment.searchPageTitle'),
    description: t('seo|treatment.searchPageDescription'),
    breadcrumbTitle: t('breadcrumb|treatment.searchTitle'),
  });
  const [searchData, setSearchData] = useState(null);

  // URL state management (IDs only)
  const {
    urlState,
    currentView,
    selections,
    updateField,
    goToResults,
    goToSteps,
    resetUrlState,
    hasValidSearchParams,
  } = useTreatmentURLState();

  // Callback to sync selection changes to URL
  const handleSelectionChange = useCallback(
    (field, values) => {
      updateField(field, values);
    },
    [updateField]
  );

  // Enriched data with labels (fetched from API on cold start)
  const {
    enrichedData,
    isLoadingLabels,
    buildSelectionLabels,
    resetEnrichedData,
    updateEnrichedField,
  } = useTreatmentEnrichedData(urlState);

  /**
   * Handle search - called when user clicks search button
   */
  const handleSearch = useCallback((data) => {
    // Merge labels: use enriched data (from bulk-initialize) as base,
    // override with step management labels where available.
    const enrichedLabels = buildSelectionLabels();
    const mergedLabels = { ...enrichedLabels };
    if (data.selectionLabels) {
      Object.entries(data.selectionLabels).forEach(([field, labels]) => {
        if (labels && labels.length > 0) {
          mergedLabels[field] = labels;
        }
      });
    }

    const searchDataWithLabels = {
      selections: data.selections,
      selectionLabels: mergedLabels,
      completedSteps: data.completedSteps,
    };
    setSearchData(searchDataWithLabels);

    // Also update enriched data with step labels for cold-start recovery on refresh
    if (data.selectionLabels) {
      Object.entries(data.selectionLabels).forEach(([field, labels]) => {
        if (labels && labels.length > 0) {
          updateEnrichedField(field, labels);
        }
      });
    }

    goToResults(data.selections);
  }, [goToResults, buildSelectionLabels, updateEnrichedField]);

  /**
   * Handle back to steps
   */
  const handleBackToSteps = useCallback(() => {
    goToSteps();
  }, [goToSteps]);

  /**
   * Handle reset - clear URL state and enriched data
   */
  const handleReset = useCallback(() => {
    resetUrlState();
    resetEnrichedData();
    setSearchData(null);
  }, [resetUrlState, resetEnrichedData]);

  /**
   * Cold start: Restore search data from URL when loading results view
   */
  useEffect(() => {
    const isResultsView = currentView === TREATMENT_VIEWS.RESULTS;
    const hasData = Boolean(searchData);

    if (isResultsView && !hasData && !isLoadingLabels) {
      if (hasValidSearchParams()) {
        const selectionLabels = buildSelectionLabels();

        // Check if labels are actually populated before setting searchData
        const hasLabels = Object.keys(selectionLabels).length > 0;
        const hasSelections = Object.keys(selections).some(
          (key) => selections[key]?.length > 0
        );

        // Only set searchData if we have labels, or if there are no selections
        if (hasLabels || !hasSelections) {
          const completedSteps = Object.keys(selections).filter(
            (key) => selections[key]?.length > 0
          ).length;
          setSearchData({
            selections,
            selectionLabels,
            completedSteps,
          });
        }
      } else {
        goToSteps();
      }
    }
  }, [currentView, searchData, isLoadingLabels, hasValidSearchParams, selections, buildSelectionLabels, goToSteps]);

  // Update breadcrumb when view changes
  useEffect(() => {
    updateBreadcrumb(currentView === TREATMENT_VIEWS.RESULTS);
  }, [currentView]);

  /**
   * Build initialSearchData from URL + enriched data for TreatmentSteps
   */
  const initialSearchData = useMemo(() => {
    const hasSelections = Object.keys(selections).some(
      (key) => selections[key]?.length > 0
    );

    if (!hasSelections) {
      return null;
    }

    return {
      selections,
      selectionLabels: buildSelectionLabels(),
      completedSteps: Object.keys(selections).filter(
        (key) => selections[key]?.length > 0
      ).length,
    };
  }, [selections, buildSelectionLabels]);

  return (
    <Spin
      spinning={isLoadingLabels}
      tip={t('loading_data', 'Chargement des données...')}
      size="large"
    >
      {currentView === TREATMENT_VIEWS.RESULTS && searchData ? (
        <TreatmentResults
          searchData={searchData}
          onBackToSteps={handleBackToSteps}
          searchMode="rapide"
        />
      ) : (
        <TreatmentSteps
          onSearch={handleSearch}
          onSelectionChange={handleSelectionChange}
          onReset={handleReset}
          initialSearchData={initialSearchData}
          enrichedData={enrichedData}
        />
      )}
    </Spin>
  );
};

export default TreatmentSearch;
